import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionId, setSessionId] = useState(null);

  const refreshUser = useCallback(async () => {
    try {
      const res = await api.get('/api/auth/profile');
      const fresh = res.data;
      localStorage.setItem('user', JSON.stringify(fresh));
      setUser(fresh);
      return fresh;
    } catch(e) { /* silent */ }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
        // Fetch fresh data in background
        api.get('/api/auth/profile').then(res => {
          localStorage.setItem('user', JSON.stringify(res.data));
          setUser(res.data);
        }).catch(() => {});
      } catch {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  // Activity heartbeat
  useEffect(() => {
    if (!user || !sessionId) return;
    const interval = setInterval(async () => {
      try {
        await api.post('/api/activity/heartbeat', {
          session_id: sessionId,
          is_idle: document.hidden
        });
      } catch (e) {
        // Silent fail
      }
    }, 60000); // Every 60 seconds
    return () => clearInterval(interval);
  }, [user, sessionId]);

  const login = useCallback(async (username, password) => {
    const res = await api.post('/api/auth/login', { username, password });
    const { token, user: userData } = res.data;
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);

    // Record login event
    try {
      const actRes = await api.post('/api/activity/login-event');
      setSessionId(actRes.data.session_id);
    } catch (e) { /* silent */ }

    return userData;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/api/activity/logout-event');
    } catch (e) { /* silent */ }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setSessionId(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, sessionId, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
